import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import "./SevTechsPage.css";

const SevTechsPage = ({ trainerID, isAdmin, isManager }) => {
	const [data, setData] = useState([]);
	const [filteredData, setFilteredData] = useState([]);
	const [roster, setRoster] = useState([]);
	const [sites, setSites] = useState([]); // Store unique site IDs
	const [selectedSite, setSelectedSite] = useState(""); // Currently selected site
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [showRoster, setShowRoster] = useState(false);
	const [showModal, setShowModal] = useState(false);

	useEffect(() => {
		fetch(`${process.env.REACT_APP_API_URL}/roster-severity/severity-all`)
			.then((response) => response.json())
			.then((data) => {
				setData(data);
				setLoading(false);

				// Extract unique site IDs for filtering
				const uniqueSites = [...new Set(data.map((tech) => tech.site_id))];
				setSites(uniqueSites);

				// Ensure filteredData updates with full list when data is fetched
				setFilteredData(data);
			})
			.catch((err) => {
				setError(err.message);
				setLoading(false);
			});
	}, []);

	// Ensure filtering applies when data or site filter changes
	useEffect(() => {
		if (selectedSite === "") {
			setFilteredData(data);
		} else {
			setFilteredData(data.filter((tech) => tech.site_id === selectedSite));
		}
	}, [selectedSite, data]);

	const handleSiteFilterChange = (event) => {
		setSelectedSite(event.target.value);
	};

	const fetchRoster = () => {
		fetch(
			`${process.env.REACT_APP_API_URL}/roster-severity/roster/${trainerID}`
		)
			.then((response) => response.json())
			.then((data) => {
				// Filter out techs who are already in SEV table (matching alias)
				const availableTechs = data.filter(
					(tech) => !data.some((sevTech) => sevTech.Alias === tech.amz_alias)
				);
				setRoster(availableTechs);
				setShowRoster(true);
			})
			.catch((err) => console.error("Error fetching roster:", err));
	};

	const addTech = (tech) => {
		const { amz_alias, site_id, wd_id, employee_name, bus_title } = tech;

		fetch(`${process.env.REACT_APP_API_URL}/roster-severity/add-tech`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				alias: amz_alias,
				siteId: site_id,
				wdId: wd_id,
				name: employee_name,
				busTitle: bus_title,
				trainerID,
			}),
		})
			.then((response) => response.json())
			.then((data) => {
				if (data.error) {
					alert(data.error); // Show alert if tech already exists
				} else {
					alert("Tech added successfully");

					// **Create New Tech Object**
					const newTech = {
						Alias: amz_alias,
						site_id: site_id,
						wd_id: wd_id,
						name: employee_name,
						bus_title: bus_title,
						sev_sts: 1, // Default active status
					};

					// **Update the SEV Table Instantly**
					setData((prevData) => [...prevData, newTech]); // Add to full data
					setFilteredData((prevData) => [...prevData, newTech]); // Add to filtered data

					// **Update Site Filters**
					setSites((prevSites) => {
						// Add the new site if it's not already in the list
						if (!prevSites.includes(site_id)) {
							return [...prevSites, site_id].sort();
						}
						return prevSites;
					});

					// **Remove the Added Tech from Roster List**
					setRoster((prevRoster) =>
						prevRoster.filter((t) => t.amz_alias !== amz_alias)
					);

					setShowModal(false);
					setShowRoster(false);
				}
			})
			.catch((err) => console.error("Error adding tech:", err));
	};

	const removeTech = (alias) => {
		fetch(`${process.env.REACT_APP_API_URL}/roster-severity/remove-tech`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ alias, trainerID }),
		})
			.then((response) => response.json())
			.then((data) => {
				if (data.error) {
					alert(data.error);
				} else {
					alert("Tech removed successfully");

					// Refresh data and filters correctly
					fetchData();
				}
			})
			.catch((err) => {
				console.error("Error removing tech:", err);
				alert("An error occurred while trying to remove the tech.");
			});
	};

	const fetchData = () => {
		fetch(`${process.env.REACT_APP_API_URL}/roster-severity/severity-all`) // Always fetch ALL sites
			.then((res) => res.json())
			.then((data) => {
				setData(data);

				// Extract **all** unique site IDs from API response, even if empty
				fetch(`${process.env.REACT_APP_API_URL}/roster-severity/all-sites`) // New API to get ALL sites
					.then((res) => res.json())
					.then((siteData) => {
						const allUniqueSites = [
							...new Set(siteData.map((site) => site.site_id)),
						];
						setSites(allUniqueSites); // Set sites from this dataset

						// If selected site has no more techs, reset to "All Sites"
						if (
							selectedSite &&
							!data.some((tech) => tech.site_id === selectedSite)
						) {
							setSelectedSite("");
							setFilteredData(data);
						} else {
							setFilteredData(
								selectedSite
									? data.filter((tech) => tech.site_id === selectedSite)
									: data
							);
						}
					});
			})
			.catch((err) => console.error("Error fetching data:", err));
	};

	const openModal = () => {
		setShowModal(true);
		fetchRoster();
	};

	const closeModal = () => {
		setShowModal(false);
		setShowRoster(false);
	};

	if (loading) {
		return <div className="loading">Loading...</div>;
	}

	return (
		<div className="container">
			{(isAdmin || isManager) && (
				<Button className="mb-3" onClick={openModal}>
					Add Tech
				</Button>
			)}

			{/* Site Filter Dropdown */}
			<div className="mb-3">
				<label htmlFor="siteFilter">Filter by Site:</label>
				<select
					id="siteFilter"
					className="form-control"
					value={selectedSite}
					onChange={handleSiteFilterChange}
				>
					<option value="">All Sites</option>
					{sites.map((site) => (
						<option key={site} value={site}>
							{site}
						</option>
					))}
				</select>
			</div>

			<h1>SEV Techs</h1>
			<div className="description-box mt-3">
				<p className="description">
					Below is a list of technicians who are experienced with SEV protocols.
					These technicians have demonstrated the required skills and are
					currently active in SEV-related tasks.
				</p>
			</div>

			<div className="table-scrollable">
				{filteredData.length > 0 ? (
					<table className="table table-striped table-hover">
						<thead className="sticky-header">
							<tr>
								<th>Alias</th>
								<th>Site ID</th>
								<th>WD ID</th>
								<th>Name</th>
								<th>Business Title</th>
								<th>SEV Status</th>
								{(isAdmin || isManager) && <th>Action</th>}
							</tr>
						</thead>
						<tbody>
							{filteredData.map((row, index) => (
								<tr key={index}>
									<td>{row.Alias}</td>
									<td>{row.site_id}</td>
									<td>{row.wd_id}</td>
									<td>{row.name}</td>
									<td>{row.bus_title}</td>
									<td>
										{row.sev_sts ? (
											<span role="img" aria-label="wrench">
												🔧
											</span>
										) : (
											"Inactive"
										)}
									</td>
									{(isAdmin || isManager) && (
										<td>
											<Button
												variant="danger"
												onClick={() => removeTech(row.Alias)}
											>
												Remove
											</Button>
										</td>
									)}
								</tr>
							))}
						</tbody>
					</table>
				) : (
					<p className="no-techs-available">No SEV Techs available.</p>
				)}
			</div>

			{/* Existing Modal for Adding Techs (Not Removed) */}
			<Modal
				show={showModal}
				onHide={closeModal}
				size="xl"
				dialogClassName="custom-modal-width"
			>
				<Modal.Header closeButton>
					<Modal.Title>Select a Tech to Add</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{showRoster ? (
						<div className="table-scrollable">
							<table className="table table-striped">
								<thead className="sticky-header">
									<tr>
										<th>Name</th>
										<th>Alias</th>
										<th>WD ID</th>
										<th>Site ID</th>
										<th>Business Title</th>
										<th>Action</th>
									</tr>
								</thead>
								<tbody>
									{roster.map((tech, index) => (
										<tr key={index}>
											<td>{tech.employee_name}</td>
											<td>{tech.amz_alias}</td>
											<td>{tech.wd_id}</td>
											<td>{tech.site_id}</td>
											<td>{tech.bus_title}</td>
											<td>
												<Button variant="success" onClick={() => addTech(tech)}>
													Add
												</Button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : (
						<p>Loading roster...</p>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={closeModal}>
						Close
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default SevTechsPage;
